export type ShortcutMode = 'current-sheet' | 'upload-files';
export type ShortcutAgentMode = 'action' | 'ask';
export type ShortcutSourceType = 'msheet' | 'csv' | 'xlsx' | 'xlsm';
export type ShortcutRunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'error';
export type ShortcutApplyAction = 'replace' | 'new_document' | 'download' | 'none';

export type ShortcutUploadResponse = {
  success: true;
  fileId: string;
};

export type ShortcutVerifyResponse = {
  success: true;
  message: string;
  webhookSecret: string;
};

export type ShortcutSubmitResponse = {
  runId: string;
  status: Extract<ShortcutRunStatus, 'queued' | 'running'>;
};

export type ShortcutRemoteListItem = {
  runId: string;
  status: ShortcutRunStatus;
  prompt: string;
  createdAt: string;
  completedAt: string | null;
  creditsUsed: number | null;
  summary: string | null;
};

export type ShortcutRemoteListResponse = {
  runs: ShortcutRemoteListItem[];
  limit: number;
  offset: number;
};

export type ShortcutCompletedRun = {
  runId: string;
  status: 'completed';
  downloadUrl: string;
  summary?: string;
  artifacts: string[];
};

export type ShortcutRunningRun = {
  runId: string;
  status: 'running';
};

export type ShortcutFailedRun = {
  runId: string;
  status: 'failed' | 'error';
  error: string;
};

export type ShortcutRemoteRun = ShortcutCompletedRun | ShortcutRunningRun | ShortcutFailedRun;

export type ShortcutRunRecord = {
  id: string;
  user_id: string;
  shortcut_run_id: string;
  source_file_path: string | null;
  source_file_name: string | null;
  source_type: ShortcutSourceType;
  selected_range: string | null;
  prompt: string;
  status: ShortcutRunStatus;
  mode: ShortcutAgentMode;
  timeout_ms: number | null;
  skills: string[];
  result_file_name: string | null;
  result_content_type: string | null;
  result_size_bytes: number | null;
  result_download_available: boolean;
  artifacts_json: string[];
  remote_summary: string | null;
  preview_cache: ShortcutPreviewCache | null;
  applied_action: ShortcutApplyAction;
  error_message: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ShortcutPreviewTableCell = string | number;
export type ShortcutPreviewTable = ShortcutPreviewTableCell[][];

export type ShortcutPreview = {
  format: 'csv' | 'xlsx' | 'xlsm';
  fileName: string;
  contentType: string;
  sheetName?: string;
  table: ShortcutPreviewTable;
  previewRows: ShortcutPreviewTable;
  rowCount: number;
  colCount: number;
  truncated: boolean;
  warning?: string;
};

export type ShortcutPreviewCache = {
  format: ShortcutPreview['format'];
  fileName: string;
  contentType: string;
  sheetName?: string;
  previewRows: ShortcutPreviewTable;
  rowCount: number;
  colCount: number;
  truncated: boolean;
  warning?: string;
};

export type ShortcutDiffCell = {
  cellId: string;
  row: number;
  col: number;
  before: string;
  after: string;
};

export type ShortcutDiffSummary = {
  changedCells: ShortcutDiffCell[];
  truncated: boolean;
};

export type ShortcutDownloadedFile = {
  blob: Blob;
  fileName: string;
  contentType: string;
  sizeBytes: number;
};

export type ShortcutRunReviewData = {
  file: ShortcutDownloadedFile;
  preview: ShortcutPreview | null;
  diff: ShortcutDiffSummary | null;
};

export type ShortcutRunDialogSubmission = {
  mode: ShortcutMode;
  prompt: string;
  agentMode: ShortcutAgentMode;
  timeoutMs?: number;
  skills: string[];
  files: File[];
};

export type ShortcutStatusTone = 'amber' | 'blue' | 'green' | 'red' | 'slate';

export function getShortcutStatusTone(status: ShortcutRunStatus): ShortcutStatusTone {
  switch (status) {
    case 'queued':
      return 'amber';
    case 'running':
      return 'blue';
    case 'completed':
      return 'green';
    case 'failed':
    case 'error':
      return 'red';
    default:
      return 'slate';
  }
}

export function isShortcutTerminalStatus(status: ShortcutRunStatus) {
  return status === 'completed' || status === 'failed' || status === 'error';
}
